import React from 'react';

import { css, cx } from '@emotion/css';

import { Icons } from '@/assets/icons.ts';
import { usePatientIdFromURL } from '@/hooks/usePatientIdFromURL.ts';
import { useStyles } from '@/hooks/useStyles.ts';
import { Queries } from '@/queries/Queries.ts';
import { Theme } from '@/theme/CreateTheme.ts';
import { Button } from '@/uiKit/atoms/Button.tsx';
import { Dot } from '@/uiKit/atoms/Dot.tsx';
import { useChatMessagingState } from '@/uiKit/organisms/ChatMessaging/ChatMessagingState.ts';

export type OpenMessagesFloatingButtonProps = {
  className?: string;
};

export const OpenMessagesFloatingButton: React.FC<
  OpenMessagesFloatingButtonProps
> = ({ className }) => {
  const styles = useStyles(makeStyles);
  const chat = useChatMessagingState(state => state);
  const patientId = usePatientIdFromURL();
  const unreadMessages = Queries.messaging.useUnreadMessages({
    patientId,
  }).total;

  if (chat.isOpen) {
    return null;
  }

  return (
    <Button
      variant="text"
      className={cx(styles.open, className)}
      onClick={() => chat.toggleChat()}
    >
      <Icons.letter className={styles.openIcon} />
      {!!unreadMessages && (
        <Dot color="error" className={styles.dot} size="caption" />
      )}
    </Button>
  );
};

const makeStyles = (theme: Theme) => ({
  open: css({
    position: 'fixed',
    top: 0,
    right: 0,
    padding: theme.spacing(2, 4),
    margin: theme.spacing(4, 0),
    backgroundColor: theme.palette.background.paper,
    height: 'fit-content',
    minWidth: 0,
    '.MuiButton-label': {
      padding: 0,
      margin: 0,
    },
  }),
  dot: css({
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(2),
  }),
  openIcon: css({
    width: 26,
    height: 26,
    color: theme.palette.text.primary,
  }),
  dotContent: css({
    whiteSpace: 'pre-wrap',
    color: 'inherit',
  }),
});
